import React from "react";
import { LucideIcon } from "lucide-react";

interface SectionCardProps {
  /** Optional small glyph icon shown top-left, ERP-style. */
  icon?: LucideIcon;
  /** Icon color class, default text-gray-400. */
  iconClassName?: string;
  /** Optional title — when omitted, only the icon glyph is shown. */
  title?: string;
  /** Title color class, default the muted ERP grey. */
  titleClassName?: string;
  /** Right-aligned controls placed in the header row. */
  actions?: React.ReactNode;
  /** Inline note shown under the title (e.g. validation hint banner). */
  note?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  /** Density of inner padding: "sm" (default ERP) or "md" (legacy). */
  density?: "sm" | "md";
  /** When true, the card body is hidden but the header stays clickable. */
  collapsed?: boolean;
  onToggle?: () => void;
  collapsible?: boolean;
}

export const SectionCard: React.FC<SectionCardProps> = ({
  icon: Icon,
  iconClassName = "text-gray-400",
  title,
  titleClassName = "text-[#8A9590]",
  actions,
  note,
  children,
  className = "",
  density = "sm",
  collapsed,
  onToggle,
  collapsible = false,
}) => {
  const innerPad = density === "sm" ? "px-3 py-2" : "p-6";

  return (
    <div
      className={`bg-white rounded-md shadow-sm border border-[rgba(11,15,14,0.08)] ${className}`}
    >
      {(Icon || title || actions) && (
        <div
          className={`flex flex-wrap items-center justify-between gap-x-3 gap-y-2 ${innerPad} ${
            children ? "border-b border-[rgba(11,15,14,0.06)]" : ""
          } ${collapsible ? "cursor-pointer select-none" : ""}`}
          onClick={collapsible ? onToggle : undefined}
        >
          <div className="flex min-w-0 flex-1 items-center gap-2">
            {Icon && <Icon className={`h-4 w-4 shrink-0 ${iconClassName}`} />}
            {title && (
              <h3
                className={`min-w-0 truncate text-[13px] font-semibold uppercase tracking-wide ${titleClassName}`}
              >
                {title}
              </h3>
            )}
          </div>
          {/* Header controls wrap onto their own line rather than pushing the
              title out of the card — a section with a filter dropdown and an
              "Add" button needs more room than a narrow card has. */}
          {actions && (
            <div
              className="flex flex-wrap items-center justify-end gap-2"

              onClick={(e) => {
                const el = e.target as HTMLElement | null;
                if (
                  el?.closest(
                    'button, a, input, select, textarea, label, [role="button"], [role="menu"], [contenteditable="true"]',
                  )
                ) {
                  e.stopPropagation();
                }
              }}
            >
              {actions}
            </div>
          )}
        </div>
      )}
      {note && (
        <div className={`px-4 py-2 bg-[#fdf5ec] border-b border-[#C97A3C]/20 text-[12px] text-[#C97A3C]`}>
          {note}
        </div>
      )}
      {/* `@container` makes the card body a container-query context, so the
          fields inside lay themselves out against the width of *this card*
          rather than the window. A card squeezed into a narrow grid column on
          a wide screen is still a narrow card. */}
      {!collapsed && children && (
        <div className={`@container ${innerPad}`}>{children}</div>
      )}
    </div>
  );
};

export default SectionCard;
